/**
 * TeamsystemImportDialog — Import PDF "Situazione scadenze analitica" TeamSystem.
 *
 * Flusso a 3 step:
 *   1. Upload PDF + selezione agente → POST /admin/scadenziario/import-teamsystem/preview
 *   2. Review → clienti riconosciuti + clienti nuovi (creati automaticamente,
 *      con possibilità di collegarli a un cliente esistente)
 *   3. Confirm → upsert idempotente delle scadenze
 *
 * Ogni riga del PDF è una scadenza: Importo = residuo aperto (0,00 = pagata).
 * Re-importare un PDF aggiornato aggiorna le scadenze esistenti senza duplicati.
 */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileText, CheckCircle2, UserPlus, X, Loader2, ArrowRight, RefreshCcw, CalendarDays, UserCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ScrollArea } from "./ui/scroll-area";
import { Input } from "./ui/input";
import { API } from "../utils/format";
import { formatMoney } from "../utils/currency";

const STEP_UPLOAD = 1;
const STEP_REVIEW = 2;
const STEP_DONE = 3;

const fmtDate = (iso) => iso ? new Date(iso + "T00:00:00").toLocaleDateString("it-IT") : "—";
const MATCH_LABEL = { code: "CODICE", name: "NOME", name_fuzzy: "NOME SIMILE" };

export default function TeamsystemImportDialog({ open, onClose, onImported, customers, agents = [] }) {
  const [step, setStep] = useState(STEP_UPLOAD);
  const [file, setFile] = useState(null);
  const [agentId, setAgentId] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [processingLabel, setProcessingLabel] = useState("");
  const [mapping, setMapping] = useState({});   // parsed_code -> customer_id (collega a esistente)
  const [unmatchedFilter, setUnmatchedFilter] = useState("");
  const [result, setResult] = useState(null);
  const [agentAssignments, setAgentAssignments] = useState({}); // customer_id -> agent_id
  const [assignAllAgent, setAssignAllAgent] = useState("");
  const [assignSaved, setAssignSaved] = useState(false);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep(STEP_UPLOAD);
    setFile(null);
    setAgentId("");
    setPreview(null);
    setSessionId(null);
    setProcessingLabel("");
    setMapping({});
    setResult(null);
    setUnmatchedFilter("");
    setAgentAssignments({});
    setAssignAllAgent("");
    setAssignSaved(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, [open]);

  useEffect(() => {
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const n = f.name.toLowerCase();
    if (!n.endsWith(".pdf") && !n.endsWith(".xlsx")) {
      toast.error("Formati ammessi: Excel (.xlsx) o PDF");
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("Il file supera 25 MB");
      return;
    }
    setFile(f);
  };

  const handlePreview = async () => {
    if (!file) return;
    setBusy(true);
    setProcessingLabel("Caricamento file sul server…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (agentId) fd.append("agent_id", agentId);
      const r = await axios.post(`${API}/admin/scadenziario/import-teamsystem/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60_000,
      });
      const sid = r.data.session_id;
      setSessionId(sid);
      setProcessingLabel("Analisi delle scadenze in corso…");
      pollRef.current = setInterval(async () => {
        try {
          const st = await axios.get(`${API}/admin/scadenziario/import-teamsystem/preview-status/${sid}`);
          if (st.data.status === "ready") {
            clearInterval(pollRef.current); pollRef.current = null;
            setPreview({ session_id: sid, summary: st.data.summary, matched: st.data.matched, unmatched: st.data.unmatched, conflicts: st.data.conflicts || [] });
            const defaults = {};
            (st.data.unmatched || []).forEach((u) => { if (u.suggested_customer_id) defaults[u.parsed_code] = u.suggested_customer_id; });
            setMapping(defaults);
            setStep(STEP_REVIEW);
            setBusy(false);
          } else if (st.data.status === "error") {
            clearInterval(pollRef.current); pollRef.current = null;
            toast.error(st.data.error || "Errore parsing PDF");
            setBusy(false);
          }
        } catch (err) {
          console.debug("poll error", err);
        }
      }, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore upload PDF");
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!sessionId) return;
    setBusy(true);
    setProcessingLabel("Importazione scadenze in corso — attendi…");
    try {
      const cleanMapping = {};
      Object.entries(mapping).forEach(([k, v]) => { if (v) cleanMapping[k] = v; });
      await axios.post(`${API}/admin/scadenziario/import-teamsystem/confirm`, {
        session_id: sessionId,
        mapping: cleanMapping,
      }, { timeout: 60_000 });
      pollRef.current = setInterval(async () => {
        try {
          const st = await axios.get(`${API}/admin/scadenziario/import-teamsystem/apply-status/${sessionId}`);
          if (st.data.status === "done") {
            clearInterval(pollRef.current); pollRef.current = null;
            setResult(st.data.result);
            setStep(STEP_DONE);
            setBusy(false);
            onImported?.();
          } else if (st.data.status === "error") {
            clearInterval(pollRef.current); pollRef.current = null;
            toast.error(st.data.error || "Errore durante l'import");
            setBusy(false);
          }
        } catch (err) {
          console.debug("poll error", err);
        }
      }, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore import");
      setBusy(false);
    }
  };

  const handleAssignAgents = async () => {
    const assignments = Object.entries(agentAssignments)
      .filter(([, v]) => v)
      .map(([customer_id, agent_id]) => ({ customer_id, agent_id }));
    if (assignments.length === 0) { toast.error("Seleziona almeno un agente"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/scadenziario/assign-agents`, { assignments });
      toast.success(`Agente assegnato a ${r.data.customers_updated} clienti`);
      setAssignSaved(true);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore assegnazione agenti");
    } finally { setBusy(false); }
  };

  const applyAgentToAll = (agentId) => {
    setAssignAllAgent(agentId);
    if (!agentId) return;
    const next = {};
    (result?.customers_needing_agent || []).forEach((c) => { next[c.customer_id] = agentId; });
    setAgentAssignments(next);
  };

  const selectedAgentName = agents.find((a) => a.id === agentId)?.name;
  const totalLinks = Object.values(mapping).filter(Boolean).length;
  const unmatchedFiltered = (preview?.unmatched || []).filter((u) => {
    if (!unmatchedFilter) return true;
    const q = unmatchedFilter.toLowerCase();
    return u.parsed_name.toLowerCase().includes(q) || u.parsed_code.includes(q);
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent className="max-w-5xl max-h-[92vh] flex flex-col" data-testid="teamsystem-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="w-5 h-5 text-[#8A1C2A]" />
            Importa scadenze da TeamSystem (Excel o PDF)
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Carica l&apos;<b>estratto conto in Excel (.xlsx)</b> — consigliato, nessun limite di pagine —
            oppure il PDF &quot;Situazione scadenze analitica&quot;. I clienti non presenti in anagrafica
            vengono <b>creati automaticamente</b> e a fine import potrai assegnarli agli agenti.
            Re-importare un file aggiornato non crea duplicati.
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 mt-2">
          {[
            [STEP_UPLOAD, "1. Carica file"],
            [STEP_REVIEW, "2. Verifica"],
            [STEP_DONE, "3. Fatto"],
          ].map(([id, label], i) => (
            <React.Fragment key={id}>
              <span className={`text-xs px-3 py-1 rounded-full border ${step === id ? "bg-[#8A1C2A] text-white border-[#8A1C2A]" : step > id ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-stone-100 text-stone-500 border-stone-200"}`}>
                {label}
              </span>
              {i < 2 && <ArrowRight className="w-3 h-3 text-stone-300" />}
            </React.Fragment>
          ))}
        </div>

        <ScrollArea className="flex-1 mt-3 pr-2 min-h-0">
          {step === STEP_UPLOAD && (
            <div className="space-y-4 py-4" data-testid="teamsystem-upload-pane">
              {/* Selezione agente */}
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
                <Label className="text-sm font-semibold text-stone-800 flex items-center gap-1.5">
                  <UserCheck className="w-4 h-4 text-[#C5A059]" />
                  Agente da assegnare (facoltativo)
                </Label>
                <p className="text-xs text-stone-500 mt-0.5 mb-2">
                  Il file non contiene l&apos;agente. Se lasci vuoto, a fine import potrai assegnare
                  l&apos;agente a ciascun cliente nuovo. Se selezioni un agente qui, verrà assegnato a tutti.
                </p>
                <Select value={agentId || "none"} onValueChange={(v) => setAgentId(v === "none" ? "" : v)} disabled={busy}>
                  <SelectTrigger className="w-72 bg-white" data-testid="teamsystem-agent-select">
                    <SelectValue placeholder="— Nessun agente —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nessun agente —</SelectItem>
                    {agents.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <label
                htmlFor="teamsystem-file"
                className={`block cursor-pointer border-2 border-dashed rounded-lg p-8 text-center transition ${busy ? "border-amber-300 bg-amber-50/40 pointer-events-none" : "border-stone-300 hover:border-[#8A1C2A]"}`}
              >
                {busy ? (
                  <>
                    <Loader2 className="w-10 h-10 text-amber-600 mx-auto mb-3 animate-spin" />
                    <p className="font-semibold text-amber-800">{processingLabel || "Elaborazione in corso…"}</p>
                    <p className="text-xs text-amber-700 mt-1">Sto analizzando il file. Attendi senza chiudere questa finestra.</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-[#C5A059] mx-auto mb-3" />
                    <p className="font-semibold text-stone-900">Trascina qui l&apos;estratto conto Excel (o il PDF) o clicca per selezionarlo</p>
                    <p className="text-xs text-stone-500 mt-1">
                      {file ? (<><b className="text-emerald-700">{file.name}</b> · {(file.size / 1024).toFixed(0)} KB pronto per l&apos;upload</>) : "Estratto conto .xlsx (consigliato) · PDF Situazione scadenze · max 25 MB"}
                    </p>
                  </>
                )}
                <input ref={inputRef} id="teamsystem-file" type="file" accept=".pdf,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleFileChange} className="hidden" data-testid="teamsystem-file-input" disabled={busy} />
              </label>
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
                <p className="font-semibold mb-1">💡 Come esportare da TeamSystem</p>
                <ol className="list-decimal pl-4 space-y-0.5">
                  <li><b>Excel (consigliato)</b>: apri l&apos;<b>Estratto conto clienti</b> ed esporta in Excel (.xlsx) — funziona anche con migliaia di righe</li>
                  <li>In alternativa: <b>Contabilità → Situazione scadenze analitica</b> → genera il PDF</li>
                  <li>Carica il file qui sopra.</li>
                </ol>
              </div>
            </div>
          )}

          {step === STEP_REVIEW && preview && (
            <div className="space-y-3 py-2" data-testid="teamsystem-review-pane">
              {busy && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-amber-700 animate-spin flex-shrink-0" />
                  <p className="text-sm text-amber-800">{processingLabel || "Applicazione in corso…"}</p>
                </div>
              )}

              {/* Info banner: data situazione + agente */}
              <div className="bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                <span className="flex items-center gap-1.5 text-stone-700" data-testid="preview-statement-date">
                  <CalendarDays className="w-4 h-4 text-[#C5A059]" />
                  Situazione al <b>{fmtDate(preview.summary.statement_date)}</b>
                </span>
                <span className="flex items-center gap-1.5 text-stone-700">
                  <UserCheck className="w-4 h-4 text-[#C5A059]" />
                  Agente: <b>{preview.summary.agent_name || selectedAgentName || "nessuno"}</b>
                </span>
                <span className="text-stone-700">
                  Totale aperto nel file: <b className="text-[#8A1C2A]">{formatMoney(preview.summary.total_open_amount, null)}</b>
                </span>
              </div>

              {/* KPI summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <SummaryCard label="Clienti nel file" value={preview.summary.customers_parsed} icon="👥" />
                <SummaryCard label="Riconosciuti" value={preview.summary.customers_matched} color="emerald" icon="✅" />
                <SummaryCard label="Nuovi (da creare)" value={preview.summary.customers_to_create} color="blue" icon="✨" />
                <SummaryCard label="Scadenze (aperte · pagate)" value={`${preview.summary.scadenze_open} · ${preview.summary.scadenze_paid}`} icon="📄" />
              </div>
              {(preview.summary.codes_to_update > 0 || preview.summary.names_to_update > 0 || preview.summary.code_conflicts > 0) && (
                <div className="grid grid-cols-3 gap-2" data-testid="teamsystem-changes-summary">
                  <SummaryCard label="Codici da aggiornare" value={preview.summary.codes_to_update || 0} color="amber" icon="🔢" />
                  <SummaryCard label="Ragioni sociali da aggiornare" value={preview.summary.names_to_update || 0} color="amber" icon="✏️" />
                  <SummaryCard label="Conflitti di codice" value={preview.summary.code_conflicts || 0} color="amber" icon="⚠️" />
                </div>
              )}
              {(preview.summary.codes_to_update > 0 || preview.summary.names_to_update > 0) && (
                <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="teamsystem-ts-rule-note">
                  <b>TeamSystem comanda:</b> codice cliente e ragione sociale dell&apos;anagrafica vengono allineati a quelli del file.
                  Il vecchio nome resta salvato come alias per la ricerca.
                </p>
              )}

              {/* Conflitti di codice */}
              {preview.conflicts?.length > 0 && (
                <div className="border border-amber-300 rounded-lg overflow-hidden" data-testid="teamsystem-conflicts-pane">
                  <div className="bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900">
                    ⚠️ Conflitti di codice ({preview.conflicts.length}) — codici occupati in anagrafica da un altro cliente
                  </div>
                  <ul className="max-h-40 overflow-y-auto text-xs divide-y divide-stone-100">
                    {preview.conflicts.map((k) => (
                      <li key={`${k.ts_code}-${k.holder_id}`} className="px-3 py-1.5" data-testid={`teamsystem-conflict-${k.ts_code}`}>
                        Codice <b className="font-mono">{k.ts_code}</b> → nel file è <b>{k.ts_name}</b>, in anagrafica è <b>{k.holder_name}</b>.{" "}
                        {k.action === "recode_from_file"
                          ? <span className="text-emerald-700">{k.holder_name} prenderà il suo codice TeamSystem <b className="font-mono">{k.new_code}</b>.</span>
                          : <span className="text-amber-800">{k.holder_name} non è nel file: riceverà il codice provvisorio <b className="font-mono">{k.new_code}</b> (da verificare).</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Matched section */}
              <div className="border border-emerald-200 rounded-lg overflow-hidden">
                <div className="bg-emerald-50 px-3 py-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-emerald-800">
                    <CheckCircle2 className="w-4 h-4 inline mr-1" />
                    Clienti riconosciuti ({preview.matched.length})
                  </span>
                </div>
                <div className="max-h-52 overflow-y-auto text-xs">
                  <table className="w-full">
                    <thead className="bg-stone-50 sticky top-0">
                      <tr className="text-left text-[10px] uppercase text-stone-500">
                        <th className="px-2 py-1.5">TeamSystem</th>
                        <th className="px-2 py-1.5">→ Giotto</th>
                        <th className="px-2 py-1.5 text-right">Scadenze</th>
                        <th className="px-2 py-1.5 text-right">Aperte</th>
                        <th className="px-2 py-1.5 text-right">Aperto €</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.matched.slice(0, 200).map((m) => (
                        <tr key={m.parsed_code} className="border-t border-stone-100" data-testid={`teamsystem-matched-${m.parsed_code}`}>
                          <td className="px-2 py-1"><span className="font-mono text-[10px] text-stone-500">{m.parsed_code}</span> {m.parsed_name}</td>
                          <td className="px-2 py-1">
                            <span className="font-mono text-[10px] text-stone-500">{m.customer_codice || "—"}</span> <span className="text-emerald-700">{m.customer_name}</span>
                            <Badge variant="outline" className="ml-1 text-[9px] py-0 border-emerald-300 text-emerald-700">{MATCH_LABEL[m.match_type] || "NOME"}</Badge>
                            {m.code_change && (
                              <Badge variant="outline" className="ml-1 text-[9px] py-0 border-amber-300 text-amber-800 bg-amber-50 font-mono" data-testid={`teamsystem-code-change-${m.parsed_code}`}>
                                codice {m.code_change.from || "—"} → {m.code_change.to}
                              </Badge>
                            )}
                            {m.name_change && (
                              <Badge variant="outline" className="ml-1 text-[9px] py-0 border-amber-300 text-amber-800 bg-amber-50" data-testid={`teamsystem-name-change-${m.parsed_code}`}>
                                nome → {m.name_change.to}
                              </Badge>
                            )}
                          </td>
                          <td className="px-2 py-1 text-right">{m.scadenze}</td>
                          <td className="px-2 py-1 text-right">{m.scadenze_aperte}</td>
                          <td className="px-2 py-1 text-right font-semibold">{formatMoney(m.open_amount, null)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Nuovi clienti (verranno creati) */}
              {preview.unmatched?.length > 0 && (
                <div className="border border-blue-300 rounded-lg overflow-hidden">
                  <div className="bg-blue-50 px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-blue-800">
                      <UserPlus className="w-4 h-4 inline mr-1" />
                      Clienti nuovi ({preview.summary.customers_to_create}) — verranno creati automaticamente
                      {selectedAgentName ? <> e assegnati a <b>{selectedAgentName}</b></> : null}
                      {totalLinks > 0 && <> · {totalLinks} collegati a esistenti (prenderanno il codice TeamSystem)</>}
                    </span>
                    <Input
                      placeholder="🔍 filtra..."
                      value={unmatchedFilter}
                      onChange={(e) => setUnmatchedFilter(e.target.value)}
                      className="h-7 text-xs w-48"
                      data-testid="teamsystem-unmatched-filter"
                    />
                  </div>
                  <div className="max-h-60 overflow-y-auto text-xs">
                    <table className="w-full">
                      <thead className="bg-stone-50 sticky top-0">
                        <tr className="text-left text-[10px] uppercase text-stone-500">
                          <th className="px-2 py-1.5">TeamSystem</th>
                          <th className="px-2 py-1.5">Oppure collega a esistente</th>
                          <th className="px-2 py-1.5 text-right">Scadenze</th>
                          <th className="px-2 py-1.5 text-right">Aperto €</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unmatchedFiltered.slice(0, 200).map((u) => (
                          <tr key={u.parsed_code} className={`border-t border-stone-100 ${mapping[u.parsed_code] ? "bg-emerald-50/40" : ""}`}>
                            <td className="px-2 py-1">
                              <span className="font-mono text-[10px] text-stone-500">{u.parsed_code}</span> {u.parsed_name}
                              {u.piva && <span className="ml-1 text-[9px] text-stone-400">P.IVA {u.piva}</span>}
                              {u.code_holders?.length > 0 && (
                                <p className="text-[10px] text-amber-800 mt-0.5" data-testid={`teamsystem-code-holder-${u.parsed_code}`}>
                                  ⚠️ in anagrafica il codice {u.parsed_code} è di <b>{u.code_holders.map((h) => h.business_name).join(", ")}</b> (nome diverso).{" "}
                                  {u.suggested_customer_id
                                    ? <>Proposto il collegamento: prenderà il nome TeamSystem. Se è un <u>altro</u> cliente scegli &quot;Crea nuovo&quot;.</>
                                    : <>Quel cliente è presente nel file con un altro codice e verrà ricodificato.</>}
                                </p>
                              )}
                              {u.suggestions?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  <span className="text-[10px] text-stone-400">Simili:</span>
                                  {u.suggestions.slice(0, 3).map((s) => (
                                    <button
                                      key={s.customer_id}
                                      type="button"
                                      onClick={() => setMapping((m) => ({ ...m, [u.parsed_code]: s.customer_id }))}
                                      className={`text-[10px] px-1.5 py-0.5 rounded border ${mapping[u.parsed_code] === s.customer_id ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-stone-50 border-stone-200 text-stone-700 hover:bg-stone-100"}`}
                                      data-testid={`teamsystem-suggest-${u.parsed_code}-${s.customer_id}`}
                                    >
                                      {s.codice || "—"} · {s.business_name}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-1">
                              <Select
                                value={mapping[u.parsed_code] || "__create__"}
                                onValueChange={(v) => setMapping((m) => ({ ...m, [u.parsed_code]: v === "__create__" ? undefined : v }))}
                              >
                                <SelectTrigger className="h-7 text-xs" data-testid={`teamsystem-map-${u.parsed_code}`}>
                                  <SelectValue placeholder="✨ crea nuovo" />
                                </SelectTrigger>
                                <SelectContent className="max-h-64">
                                  <SelectItem value="__create__">✨ Crea nuovo cliente</SelectItem>
                                  {(customers || []).map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                      {c.codice_cliente || "—"} · {c.business_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-2 py-1 text-right">{u.scadenze}</td>
                            <td className="px-2 py-1 text-right">{formatMoney(u.open_amount, null)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === STEP_DONE && result && (
            <div className="py-4 space-y-4" data-testid="teamsystem-result-pane">
              <div className="text-center">
                <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-2" />
                <h3 className="text-lg font-semibold text-stone-900">Import completato con successo!</h3>
                <p className="text-sm text-stone-500">
                  Scadenziario aggiornato alla situazione del <b>{fmtDate(result.statement_date)}</b>
                  {result.agent_name ? <> · agente <b>{result.agent_name}</b></> : null}.
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-w-2xl mx-auto">
                <ResultCard label="Scadenze create" value={result.stats.scadenze_created} color="emerald" />
                <ResultCard label="Scadenze aggiornate" value={result.stats.scadenze_updated} color="blue" />
                <ResultCard label="Invariate" value={result.stats.scadenze_unchanged} color="stone" />
                <ResultCard label="Clienti creati" value={result.stats.customers_created} color="emerald" />
                <ResultCard label="Agente assegnato" value={result.stats.customers_agent_assigned} color="blue" />
                {result.stats.customers_code_updated > 0 && (
                  <ResultCard label="Codici aggiornati" value={result.stats.customers_code_updated} color="amber" />
                )}
                {result.stats.customers_renamed > 0 && (
                  <ResultCard label="Ragioni sociali aggiornate" value={result.stats.customers_renamed} color="amber" />
                )}
                {result.stats.customers_temp_code > 0 && (
                  <ResultCard label="Codici provvisori" value={result.stats.customers_temp_code} color="amber" />
                )}
                {result.stats.old_format_docs_removed > 0 && (
                  <ResultCard label="Vecchi doc. rimossi" value={result.stats.old_format_docs_removed} color="amber" />
                )}
              </div>
              {(result.customers_code_updates || []).length > 0 && (
                <details className="bg-amber-50 border border-amber-200 rounded p-2 text-xs max-w-2xl mx-auto" data-testid="result-code-updates">
                  <summary className="cursor-pointer font-semibold text-amber-900">
                    {result.customers_code_updates.length} codici cliente allineati a TeamSystem
                  </summary>
                  <ul className="mt-2 max-h-40 overflow-y-auto text-amber-900 pl-4 list-disc">
                    {result.customers_code_updates.map((c) => (
                      <li key={c.customer_id}>{c.business_name}: <span className="font-mono">{c.from || "—"} → {c.to}</span></li>
                    ))}
                  </ul>
                </details>
              )}
              {(result.customers_name_updates || []).length > 0 && (
                <details className="bg-amber-50 border border-amber-200 rounded p-2 text-xs max-w-2xl mx-auto" data-testid="result-name-updates">
                  <summary className="cursor-pointer font-semibold text-amber-900">
                    {result.customers_name_updates.length} ragioni sociali allineate a TeamSystem
                  </summary>
                  <ul className="mt-2 max-h-40 overflow-y-auto text-amber-900 pl-4 list-disc">
                    {result.customers_name_updates.map((c) => (
                      <li key={c.customer_id}>{c.from} → <b>{c.to}</b></li>
                    ))}
                  </ul>
                </details>
              )}
              {(result.customers_temp_coded || []).length > 0 && (
                <details open className="bg-amber-50 border border-amber-300 rounded p-2 text-xs max-w-2xl mx-auto" data-testid="result-temp-coded">
                  <summary className="cursor-pointer font-semibold text-amber-900">
                    ⚠️ {result.customers_temp_coded.length} clienti con codice provvisorio da verificare
                  </summary>
                  <ul className="mt-2 max-h-40 overflow-y-auto text-amber-900 pl-4 list-disc">
                    {result.customers_temp_coded.map((c) => (
                      <li key={c.customer_id}>{c.business_name}: <span className="font-mono">{c.from} → {c.to}</span> — il codice {c.from} in TeamSystem è di {c.taken_by}</li>
                    ))}
                  </ul>
                </details>
              )}
              {(result.customers_created_names || []).length > 0 && (
                <details className="bg-blue-50 border border-blue-200 rounded p-2 text-xs max-w-2xl mx-auto">
                  <summary className="cursor-pointer font-semibold text-blue-800">
                    {result.customers_created_names.length} clienti creati in anagrafica
                  </summary>
                  <ul className="mt-2 max-h-40 overflow-y-auto text-blue-900 pl-4 list-disc">
                    {result.customers_created_names.map((n) => (<li key={n}>{n}</li>))}
                  </ul>
                </details>
              )}

              {/* Assegnazione agenti ai clienti nuovi (il file non contiene l'agente) */}
              {(result.customers_needing_agent || []).length > 0 && !assignSaved && (
                <div className="border-2 border-amber-300 rounded-lg overflow-hidden max-w-3xl mx-auto" data-testid="assign-agents-pane">
                  <div className="bg-amber-50 px-3 py-2.5 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-amber-900">
                      <UserCheck className="w-4 h-4 inline mr-1" />
                      Assegna l&apos;agente ai {result.customers_needing_agent.length} clienti nuovi
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-amber-800">Assegna tutti a:</span>
                      <Select value={assignAllAgent || "none"} onValueChange={(v) => applyAgentToAll(v === "none" ? "" : v)}>
                        <SelectTrigger className="h-7 text-xs w-44 bg-white" data-testid="assign-all-agent-select">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          {agents.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto text-xs bg-white">
                    <table className="w-full">
                      <tbody>
                        {result.customers_needing_agent.map((c) => (
                          <tr key={c.customer_id} className={`border-t border-stone-100 ${agentAssignments[c.customer_id] ? "bg-emerald-50/40" : ""}`}>
                            <td className="px-3 py-1.5">
                              <span className="font-mono text-[10px] text-stone-500">{c.codice_cliente}</span> {c.business_name}
                            </td>
                            <td className="px-3 py-1.5 w-52">
                              <Select
                                value={agentAssignments[c.customer_id] || "none"}
                                onValueChange={(v) => setAgentAssignments((m) => ({ ...m, [c.customer_id]: v === "none" ? undefined : v }))}
                              >
                                <SelectTrigger className="h-7 text-xs" data-testid={`assign-agent-${c.codice_cliente}`}>
                                  <SelectValue placeholder="— scegli agente —" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">— nessuno —</SelectItem>
                                  {agents.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="bg-amber-50 px-3 py-2 flex items-center justify-between gap-2">
                    <p className="text-[11px] text-amber-800">Puoi anche saltare e assegnarli dopo dall&apos;anagrafica clienti.</p>
                    <Button size="sm" onClick={handleAssignAgents} disabled={busy || Object.values(agentAssignments).filter(Boolean).length === 0} className="bg-[#8A1C2A] hover:bg-[#6e1622] text-white h-8" data-testid="save-agent-assignments-btn">
                      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                      Salva assegnazioni ({Object.values(agentAssignments).filter(Boolean).length})
                    </Button>
                  </div>
                </div>
              )}
              {assignSaved && (
                <p className="text-center text-sm text-emerald-700 font-semibold" data-testid="assignments-saved-msg">
                  ✅ Agenti assegnati con successo
                </p>
              )}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="flex-row items-center justify-between gap-2 pt-3 border-t">
          <div className="text-xs text-stone-500">
            {step === STEP_REVIEW && preview && (
              <>Sessione: <span className="font-mono">{preview.session_id.slice(0, 8)}…</span></>
            )}
          </div>
          <div className="flex gap-2">
            {step === STEP_UPLOAD && (
              <>
                <Button variant="outline" onClick={onClose} disabled={busy}><X className="w-4 h-4 mr-1" />Annulla</Button>
                <Button onClick={handlePreview} disabled={!file || busy} className="bg-[#8A1C2A] hover:bg-[#6e1622] text-white" data-testid="teamsystem-analyze-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                  Analizza file
                </Button>
              </>
            )}
            {step === STEP_REVIEW && (
              <>
                <Button variant="outline" onClick={() => setStep(STEP_UPLOAD)} disabled={busy}><X className="w-4 h-4 mr-1" />Indietro</Button>
                <Button onClick={handleConfirm} disabled={busy} className="bg-[#8A1C2A] hover:bg-[#6e1622] text-white" data-testid="teamsystem-confirm-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                  Conferma e importa
                </Button>
              </>
            )}
            {step === STEP_DONE && (
              <>
                <Button variant="outline" onClick={() => { setStep(STEP_UPLOAD); setFile(null); setPreview(null); setMapping({}); setResult(null); setAgentAssignments({}); setAssignAllAgent(""); setAssignSaved(false); }}>
                  <RefreshCcw className="w-4 h-4 mr-1" />Importa altro file
                </Button>
                <Button onClick={onClose} className="bg-[#8A1C2A] hover:bg-[#6e1622] text-white">Chiudi</Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SummaryCard({ label, value, color = "stone", icon }) {
  const colors = {
    stone: "bg-stone-50 border-stone-200 text-stone-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
  };
  return (
    <div className={`rounded border p-2 ${colors[color] || colors.stone}`}>
      <p className="text-[10px] uppercase font-semibold tracking-wide opacity-70">{icon} {label}</p>
      <p className="text-xl font-bold mt-0.5">{value}</p>
    </div>
  );
}


function ResultCard({ label, value, color }) {
  const colors = {
    stone: "bg-stone-50 text-stone-700 border-stone-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    blue: "bg-blue-50 text-blue-800 border-blue-200",
  };
  return (
    <div className={`p-2 rounded border ${colors[color]}`}>
      <p className="text-[10px] uppercase font-semibold tracking-wide opacity-70">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}
